import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit3,
  Archive,
  Trash2,
  MessageSquare,
  Users,
  User,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

import { chatApi } from '@/services/chat-api';
import { handleApiError } from '@/services/api';
import { useTimezone } from '@/contexts/TimezoneContext';
import { ConfirmationModal } from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { ChatThread } from '@/types/chat';

type ChatTab = 'mine' | 'team';

const ChatHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatRelativeTime } = useTimezone();

  const [activeTab, setActiveTab] = useState<ChatTab>('mine');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ChatThread | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────

  const { data: threadsData, isLoading } = useQuery({
    queryKey: ['chat-threads', 'history'],
    queryFn: () => chatApi.listThreads({ limit: 50 }),
    staleTime: 30_000,
  });

  const { data: teamData, isLoading: isLoadingTeam } = useQuery({
    queryKey: ['chat-threads', 'team'],
    queryFn: () => chatApi.listTeamThreads({ limit: 50 }),
    staleTime: 30_000,
    enabled: activeTab === 'team',
  });

  const myThreads: ChatThread[] = threadsData?.data ?? [];
  const teamThreads: ChatThread[] = teamData?.data ?? [];
  const threads = activeTab === 'mine' ? myThreads : teamThreads;

  const filteredThreads = useMemo(() => {
    if (!searchQuery.trim()) return threads;
    const query = searchQuery.toLowerCase();
    return threads.filter((t) =>
      (t.title || 'Untitled chat').toLowerCase().includes(query)
    );
  }, [threads, searchQuery]);

  // ── Mutations ────────────────────────────────────────────────────────

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      chatApi.updateThread(id, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      setEditingThreadId(null);
      toast.success('Chat renamed');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to rename');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => chatApi.archiveThread(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      toast.success('Chat archived');
    },
    onError: () => toast.error('Failed to archive'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => chatApi.deleteThread(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      setDeleteTarget(null);
      toast.success('Chat deleted');
    },
    onError: () => {
      setDeleteTarget(null);
      toast.error('Failed to delete');
    },
  });

  // ── Inline rename handlers ───────────────────────────────────────────

  const startRename = (thread: ChatThread): void => {
    setEditingThreadId(thread.id);
    setEditingTitle(thread.title || '');
  };

  const commitRename = (): void => {
    if (!editingThreadId) return;
    const trimmed = editingTitle.trim();
    if (trimmed) {
      renameMutation.mutate({ id: editingThreadId, title: trimmed });
    } else {
      setEditingThreadId(null);
    }
  };

  const cancelRename = (): void => {
    setEditingThreadId(null);
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col items-center min-h-screen px-4 py-8 sm:py-12">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-foreground">
            Chats
          </h1>
          <button
            onClick={() => navigate('/chat')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors"
          >
            <Plus className="w-4 h-4" />
            New chat
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit border border-transparent dark:border-white/10 mb-6">
          <button
            onClick={() => { setActiveTab('mine'); setSearchQuery(''); }}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              activeTab === 'mine'
                ? 'bg-white dark:bg-white/10 text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <User className="w-3.5 h-3.5" />
            My Chats
          </button>
          <button
            onClick={() => { setActiveTab('team'); setSearchQuery(''); }}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              activeTab === 'team'
                ? 'bg-white dark:bg-white/10 text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Users className="w-3.5 h-3.5" />
            Team Chats
          </button>
        </div>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={activeTab === 'mine' ? 'Search your chats...' : 'Search team chats...'}
            className={clsx(
              'w-full pl-10 pr-4 py-2.5 rounded-xl text-sm',
              'bg-muted',
              'border border-border',
              'text-foreground',
              'placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40',
              'transition-colors'
            )}
          />
        </div>

        {/* Subheader */}
        <p className="text-sm text-muted-foreground mb-4">
          {activeTab === 'mine' ? 'Your chats with Zamino' : 'Chats shared by your team'}
        </p>

        {/* Thread list */}
        {(activeTab === 'mine' ? isLoading : isLoadingTeam) ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <MessageSquare className="w-10 h-10 text-muted-foreground/60 mb-3" />
            <p className="text-muted-foreground text-sm">
              {searchQuery.trim()
                ? 'No chats matching your search'
                : activeTab === 'team'
                  ? 'No shared team chats yet'
                  : 'No conversations yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredThreads.map((thread) => (
              <HistoryThreadRow
                key={thread.id}
                thread={thread}
                isTeam={activeTab === 'team'}
                isEditing={editingThreadId === thread.id}
                editingTitle={editingTitle}
                onEditingTitleChange={setEditingTitle}
                onCommitRename={commitRename}
                onCancelRename={cancelRename}
                onStartRename={startRename}
                onArchive={() => archiveMutation.mutate(thread.id)}
                onDelete={() => setDeleteTarget(thread)}
                onClick={() => navigate(`/chat/${thread.id}`)}
                formatRelativeTime={formatRelativeTime}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete conversation"
        message={`Are you sure you want to delete "${deleteTarget?.title || 'Untitled chat'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

// ── Thread row component ─────────────────────────────────────────────────

interface HistoryThreadRowProps {
  thread: ChatThread;
  isTeam: boolean;
  isEditing: boolean;
  editingTitle: string;
  onEditingTitleChange: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onStartRename: (thread: ChatThread) => void;
  onArchive: () => void;
  onDelete: () => void;
  onClick: () => void;
  formatRelativeTime: (date: string | Date) => string;
}

const HistoryThreadRow: React.FC<HistoryThreadRowProps> = ({
  thread,
  isTeam,
  isEditing,
  editingTitle,
  onEditingTitleChange,
  onCommitRename,
  onCancelRename,
  onStartRename,
  onArchive,
  onDelete,
  onClick,
  formatRelativeTime,
}) => {
  const title = thread.title || 'Untitled chat';
  const timeAgo = thread.last_message_at
    ? formatRelativeTime(thread.last_message_at)
    : formatRelativeTime(thread.created_at);

  return (
    <div
      className={clsx(
        'group flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors',
        'hover:bg-muted'
      )}
      onClick={isEditing ? undefined : onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Thread info */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            type="text"
            value={editingTitle}
            onChange={(e) => onEditingTitleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCommitRename();
              if (e.key === 'Escape') onCancelRename();
            }}
            onBlur={onCommitRename}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            className={clsx(
              'w-full px-2 py-0.5 text-[15px] font-semibold rounded',
              'border border-primary focus:outline-none focus:ring-2 focus:ring-ring/40',
              'bg-card text-foreground'
            )}
          />
        ) : (
          <p className="text-[15px] font-semibold text-foreground truncate">
            {title}
          </p>
        )}
        <p className="text-[13px] text-muted-foreground mt-0.5">
          {isTeam && thread.author_name && (
            <span className="text-primary mr-1.5">{thread.author_name}</span>
          )}
          {isTeam && thread.shared_at
            ? `Shared ${formatRelativeTime(thread.shared_at)}`
            : `Last message ${timeAgo}`}
        </p>
      </div>

      {/* Actions menu (visible on hover, own threads only) */}
      {!isEditing && !isTeam && (
        <div
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Menu as="div" className="relative">
            <Menu.Button
              className={clsx(
                'p-1.5 rounded-lg transition-colors',
                'text-muted-foreground hover:text-foreground',
                'hover:bg-accent'
              )}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-1 w-44 origin-top-right rounded-xl bg-card shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none z-10">
                <div className="p-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => onStartRename(thread)}
                        className={clsx(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                          'text-foreground',
                          active && 'bg-accent'
                        )}
                      >
                        <Edit3 className="w-4 h-4" />
                        Rename
                      </button>
                    )}
                  </Menu.Item>
                  {thread.status !== 'archived' && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={onArchive}
                          className={clsx(
                            'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                            'text-foreground',
                            active && 'bg-accent'
                          )}
                        >
                          <Archive className="w-4 h-4" />
                          Archive
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={onDelete}
                        className={clsx(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                          'text-destructive',
                          active && 'bg-destructive/15'
                        )}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      )}
    </div>
  );
};

export default ChatHistoryPage;
